import { Component, AfterViewInit, OnDestroy, TemplateRef, ViewChild, Inject, Input } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { Subscription, filter, switchMap, tap } from 'rxjs';

import { TabComponent } from '../../../Layout/components/Tab/tab.component';
import { TargetingSearchService } from '../../../../Discover/Targeting/services/targeting-search.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { TableColumnModel } from '../../../Forms/models/table-column.model';
import { TableColumnInputEnum } from '../../../Forms/enums/table-column-input-type.enum';
import { UsersService } from '../../../../Core/Users/services/users.service';
import { DateHelper } from '../../../Forms/helpers/date.helper';
import { TargetingListService } from '../../../../Discover/Targeting/services/targeting-list.service';
import { DataRow } from '../ListPickerDialog/list-picker-dialog.component';
import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { ManageListDialogComponent } from '../../../../Discover/Targeting/components/TargetingResults/ManageListDialog/manage-list-dialog.component';
import { ContactModel } from '../../../TargetingShared/model/contact.model';
import { OutletModel } from '../../../../Discover/Targeting/models/outlet.model';
import { PrivateDataModel } from '../../../../Discover/Targeting/models/private-data.model';
import { TargetingRhpContextsService } from '../../../TargetingShared/services/targeting-rhp-contexts.service';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';

@Component({
    selector: 'agpr-list-membership-tab',
    templateUrl: './list-membership-tab.component.html',
    styleUrls: ['./list-membership-tab.component.scss'],
    providers: [
        {
            provide: TabComponent,
            useExisting: ListMembershipTabComponent
        }
    ],
    standalone: false
})
export class ListMembershipTabComponent extends TabComponent implements OnDestroy, AfterViewInit {

    @Input() hideBusinessCard: boolean = false;

    @ViewChild('tabHeader') declare _header: TemplateRef<any>;
    public get header(): TemplateRef<any> {
        return this._header;
    }
    public set header(value: TemplateRef<any>) {
        this._header = value;
    }
    @ViewChild('tabFooter') declare footer: TemplateRef<any>;
    @ViewChild('countInfoCell') public countCellTemplate: TemplateRef<any>;
    @ViewChild('nameListCell') public nameListCellTemplate: TemplateRef<any>;
    @ViewChild('handlerCell') public handlerCellTemplate: TemplateRef<any>;

    loading: boolean = true;
    dataRows: DataRow[];
    filteredRows: DataRow[];

    columnDefs: TableColumnModel[] = [];
    filterString: string = '';
    focusedRhpResult: ContactModel | OutletModel;
    contactPrivateData: PrivateDataModel;
    outletPrivateData: PrivateDataModel;
    listMembershipIds: number[] = [];

    dateFormat: string;

    private subscriptions: Subscription[] = [];

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly entityType = EntityType;

    nameColumn: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        heading: 'Discover.listBuilder.table.listname',
        field: 'name',
        sortable: true,
        sortType: 'text',
        maxWidth: 50,
        tooltipField: 'createdByUser'
    };

    countColumn: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        heading: 'Discover.listBuilder.table.contacts',
        field: 'count',
        sortable: false,
        styleClass: 'agpr-listMembership-countHeader',
        cellStyleClass: 'agpr-listMembership-countColumn',
    };

    handlerColumn: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        sortable: false,
        cellStyleClass: 'agpr-listMembership-handlerColumn'
    };


    constructor(
        public targetingSearchService: TargetingSearchService,
        private coreDialogService: CoreDialogService,
        public usersService: UsersService,
        public targetingListService: TargetingListService,
        public targetingRhpContextsService: TargetingRhpContextsService,
        @Inject(APP_BASE_HREF) private appBaseHref: string,
        public currentUserStateService: CurrentUserStateService
    ) {
        super();
        this.subscriptions.push(
            this.targetingRhpContextsService.getContact().subscribe(contact => {
                if (contact) {
                    this.focusedRhpResult = contact;
                    this.getListMemberships(EntityType.CONTACT, contact.id);
                }
            }),
            this.targetingRhpContextsService.getOutlet().subscribe(x => {
                if (x.outlet && x.entityType === EntityType.OUTLET) {
                    this.focusedRhpResult = x.outlet;
                    this.getListMemberships(EntityType.OUTLET, x.outlet.id);
                }
            }),
            this.targetingRhpContextsService.getContactPrivateData().pipe(filter(x => !!x)).subscribe(privateData => {
                this.contactPrivateData = privateData;
            }),
            this.targetingRhpContextsService.getOutletPrivateData().pipe(filter(x => !!x)).subscribe(privateData => {
                this.outletPrivateData = privateData;
            }),
            this.targetingListService.all$.subscribe(x => {
                if (this.focusedRhpResult) {
                    this.getListMemberships(this.focusedRhpResult.entityType, this.focusedRhpResult.id);
                }
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x))
                .subscribe(prefs => this.dateFormat = prefs.getDateFormatString(true, false, false)),
        );
    }

    getListMemberships(entityType: EntityType, entityId: string) {
        const entityListKey = entityType === EntityType.CONTACT ? 'contactUuid' : 'outletUuid';
        this.subscriptions.push(
            this.targetingListService.getListMemberships([{
                [entityListKey]: entityId
            }]).pipe(
                tap(listMemberships => {
                    this.listMembershipIds = listMemberships[0]?.targetingLists?.map(list => list.id) || [];
                    this.targetingRhpContextsService.setListMembershipIds(this.listMembershipIds);
                }),
                switchMap(() => this.targetingListService.all$),
                filter(x => !!x)
            ).subscribe(lists => {
                this.dataRows = lists.filter(list => this.listMembershipIds.includes(list.id)).map(list => {
                    return {
                        id: list.id,
                        selected: false,
                        name: list.name,
                        count: list.count,
                        originalData: list,
                        createdDate: DateHelper.toDateString(list.createdDate, null, this.dateFormat),
                        modifiedDate: DateHelper.toDateString(list.modifiedDate, null, this.dateFormat),
                    };
                });
                this.filteredRows = this.dataRows;
                this.fetchUserNames();
            })
        );
    }

    ngAfterViewInit() {
        requestAnimationFrame(() => {
            this.countColumn.customTemplate = this.countCellTemplate;
            this.nameColumn.customTemplate = this.nameListCellTemplate;
            this.handlerColumn.customTemplate = this.handlerCellTemplate;
            this.columnDefs = [this.nameColumn, this.countColumn, this.handlerColumn];
        });
    }

    /**
     * For the info tooltip, convert user ids to usernames
     */
    private fetchUserNames() {
        const userIds = [...this.dataRows.map(x => +x.originalData.createdByUserId),
            ...this.dataRows.map(x => +x.originalData.modifiedByUserId)];
        this.subscriptions.push(
            this.usersService.getUsers(userIds).subscribe(users => {
                this.dataRows.forEach(row => {
                    const creator = users.find(x => x.id === row.originalData.createdByUserId);
                    if (creator) {
                        row.createdUser = creator.getDisplayName();
                    }

                    const modifier = users.find(x => x.id === row.originalData.modifiedByUserId);
                    if (modifier) {
                        row.modifiedUser = modifier.getDisplayName();
                    }
                });
            })
        );
    }

    filterRows() {
        if (this.filterString?.length === 0) {
            this.filteredRows = this.dataRows;
        }
        this.filteredRows = this.dataRows.filter(row => row.name?.toLowerCase().indexOf(this.filterString?.toLowerCase()) > -1);
    }

    openTargetingListTab(row: DataRow) {
        window.open(this.appBaseHref + `Discover/targetinglists/list/${row?.id}/results`, '_blank');
    }

    openManageListDialog() {
        this.coreDialogService.open(ManageListDialogComponent, {
            inputs: {
                setEntity: this.focusedRhpResult
            },
            outputs: {
                listMembershipChanged: () => {
                    this.getListMemberships(this.focusedRhpResult.entityType, this.focusedRhpResult.id);
                }
            }
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

}
